import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import compact from 'lodash/compact';
import find from 'lodash/find';
import isBoolean from 'lodash/isBoolean';
import get from 'lodash/get';
import head from 'lodash/head';
import isString from 'lodash/isString';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import tail from 'lodash/tail';
import { format as currencyFormat } from 'currency-formatter';
import DashboardBreadcrumb from '../DashboardBreadcrumb/DashboardBreadcrumb';
import ModalPortal from '../ModalPortal/ModalPortal';
import FormattedAddress from '../FormattedAddress';
import * as analytics from '../../services/analytics';
import './BOAPolicyDetailsView.css';

const propTypes = {
  client: PropTypes.object,
};

class BOAPolicyDetailsView extends PureComponent {
  constructor(props) {
    super(props);
    this.coverageKeys = {
      owner: {
        categoryKey: 'business_owner_coverage',
        detailKeys: [
          'business_owner_accidental_death_and_dismemberment',
          'business_owner_paralysis',
          'business_owner_cobra_premium_expense',
          'business_owner_permanent_total_disability',
          'business_owner_temporary_total_disability',
          'business_owner_enhanced_in_hospital',
        ],
      },
      employee: {
        categoryKey: 'employee_coverage',
        detailKeys: [
          'employee_accidental_death_and_dismemberment_assault',
          'employee_accidental_death_and_dismemberment_business',
          'employee_paralysis',
          'employee_cobra_premium_expense',
          'employee_permanent_total_disability',
          'employee_temporary_total_disability',
          'employee_enhanced_in_hospital',
        ],
      },
    };
    this.state = {
      modalTitle: null,
      modalBody: null,
    };
  }

  componentDidMount() {
    analytics.page('Broker Portal - Policy Details');
    this.props.getPolicies({ clientId: this.props.client.id });
  }

  componentWillUnmount() {
    window.$(this.modal).modal('hide');
  }

  getBadgeForStatus(status) {
    switch (status) {
      case 'inforce':
        return (
          <span className="badge badge-success text-uppercase">In force</span>
        );
      case 'canceled':
        return (
          <span className="badge badge-danger text-uppercase">Canceled</span>
        );
      default:
        return 'n/a';
    }
  }

  getCoverageDetailsValue(value) {
    if (isBoolean(value)) {
      return value ? 'Included' : 'Not Included';
    } else {
      return value || 'n/a';
    }
  }

  getCellText = (items) => {
    return items.map((item, i) => {
      if (i + 1 < items.length) {
        return `${item}\n`;
      } else {
        return item;
      }
    });
  };

  showInfoModal = (e) => {
    analytics.track('Broker Portal - Coverage Details Modal Opened', {
      title: e.currentTarget.dataset.modalTitle,
    });
    this.setState(
      {
        modalTitle: e.currentTarget.dataset.modalTitle,
        modalBody: JSON.parse(e.currentTarget.dataset.modalBody),
      },
      () => {
        window.$(this.modal).modal('show');
      }
    );
  };

  trackPolicyDocClick = (e) => {
    analytics.track('Broker Portal - External Link Clicked', {
      label: e.target.dataset.docName,
    });
  };

  renderCoverageDetails = (category, subcategory) => {
    const coverageDetailKeys = this.coverageKeys[category].detailKeys;
    const categoryKey = this.coverageKeys[category].categoryKey;
    let subcategoryKey;
    switch (subcategory) {
      case 'primary':
        subcategoryKey = 'primary_benefits';
        break;
      case 'additional':
        subcategoryKey = 'additional_benefits';
        break;
      default:
        break;
    }

    const coverageDetails = get(
      this.props.policy.plan,
      `settings.details.coverage_details.${categoryKey}.${subcategoryKey}`
    );

    if (coverageDetails) {
      return compact(
        coverageDetailKeys.map((key) => {
          const coverageDetail = get(coverageDetails, key);
          if (coverageDetail) {
            const title = get(coverageDetail, 'title');
            const description = get(coverageDetail, 'description');
            return (
              <tr key={key}>
                <td>
                  {title}{' '}
                  {description && (
                    <span
                      data-modal-title={title}
                      data-modal-body={JSON.stringify(description)}
                      onClick={this.showInfoModal}
                    >
                      <i className="BOAPolicyDetailsView-infoIcon fas fa-info-circle" />
                    </span>
                  )}
                </td>
                <td>{this.getCellText(coverageDetail.coverage_details)}</td>
                <td>{this.getCellText(coverageDetail.value_details)}</td>
              </tr>
            );
          } else {
            return null;
          }
        })
      );
    } else {
      return null;
    }
  };

  coverageTable = (category) => (
    <table className="BOAPolicyDetailsView-table table table-sm table-striped">
      <thead>
        <tr>
          <th>Primary Benefits</th>
          <th>Hazard for which coverage applies</th>
          <th>Benefit amount</th>
        </tr>
      </thead>
      <tbody>{this.renderCoverageDetails(category, 'primary')}</tbody>
      <thead>
        <tr>
          <th>Additional Benefits</th>
          <th>Hazard for which coverage applies</th>
          <th>Benefit amount</th>
        </tr>
      </thead>
      <tbody>{this.renderCoverageDetails(category, 'additional')}</tbody>
    </table>
  );

  get modalBody() {
    if (isString(this.state.modalBody)) {
      return <div className="modal-body">{this.state.modalBody}</div>;
    } else if (isArray(this.state.modalBody)) {
      const tableHeaders = head(this.state.modalBody);
      const tableValues = tail(this.state.modalBody);
      return (
        <table className="table modal-table table-striped BOAPolicyDetailsView-modalTable">
          <thead>
            <tr>
              <th>{tableHeaders.title}</th>
              <th>{tableHeaders.value}</th>
            </tr>
          </thead>
          <tbody>
            {tableValues.map((item) => (
              <tr key={item.title}>
                <td>{item.title}</td>
                <td>{item.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else {
      return null;
    }
  }

  render() {
    const { client, policy } = this.props;
    const bill = get(policy, 'bills[0]');
    const currencyOpts = {
      code: bill.currency.code,
    };

    return (
      <>
        <div className="BOAPolicyDetailsView">
          <DashboardBreadcrumb client={client} policy={policy} />
          <div className="container-fluid">
            <h2 className="BOAPolicyDetailsView-heading">Policy details</h2>
            <div className="row">
              <div className="col-md-4">
                <div className="card">
                  <h3 className="BOAPolicyDetailsView-sectionTitle">
                    Overview
                  </h3>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td>Policy number</td>
                        <td>{policy.policy_number || 'n/a'}</td>
                      </tr>
                      <tr>
                        <td>Date issued</td>
                        <td>
                          {moment(policy.created_at).format('MMM DD, YYYY')}
                        </td>
                      </tr>
                      <tr>
                        <td>Term</td>
                        <td>
                          {moment(policy.starts_at, 'YYYY-MM-DD').format(
                            'MMM DD, YYYY'
                          )}{' '}
                          to{' '}
                          {moment(policy.ends_at, 'YYYY-MM-DD').format(
                            'MMM DD, YYYY'
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>Status</td>
                        <td>{this.getBadgeForStatus(policy.status)}</td>
                      </tr>
                      {policy.payment_method && (
                        <tr>
                          <td>Payment method</td>
                          <td>{policy.payment_method.name}</td>
                        </tr>
                      )}
                      {map(policy.policy_documents, (doc, i) => (
                        <tr key={i}>
                          <td>{doc.name}</td>
                          <td>
                            <a
                              className="text-complete"
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-doc-name={doc.name}
                              onClick={this.trackPolicyDocClick}
                            >
                              <i className="fas fa-download" /> Download
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="col-md-8">
                <div className="card">
                  <h3 className="BOAPolicyDetailsView-sectionTitle">
                    Policy holder details
                  </h3>
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Mailing address</th>
                        <th>Date of birth</th>
                        <th>Email</th>
                        <th>Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {policy.policy_holders.map((policyholder) => (
                        <tr key={policyholder.person.id}>
                          <td>{policyholder.person.business_name}</td>
                          <td>
                            <FormattedAddress
                              address={policyholder.person.address}
                            />
                          </td>
                          <td>
                            {(get(policyholder, 'person.dob') &&
                              moment(
                                get(policyholder, 'person.dob'),
                                'YYYY-MM-DD'
                              ).format('MM/DD/YYYY')) ||
                              'n/a'}
                          </td>
                          <td>{policyholder.person.email}</td>
                          <td>{policyholder.person.phone_number}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="card">
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td
                          colSpan="2"
                          className="BOAPolicyDetailsView-sectionTitle"
                        >
                          Coverage details
                        </td>
                      </tr>
                      <tr>
                        <td>Covered business</td>
                        <td>
                          {
                            find(policy.policy_holders, {
                              position_on_policy: 1,
                            }).person.business_name
                          }
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="BOAPolicyDetailsView-tableHeading">
                    For Business Owners
                  </div>
                  {this.coverageTable('owner')}
                  <div className="BOAPolicyDetailsView-tableHeading">
                    For Employees
                  </div>
                  {this.coverageTable('employee')}
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td
                          colSpan="2"
                          className="BOAPolicyDetailsView-sectionTitle"
                        >
                          Price
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Total plan price</strong>
                        </td>
                        <td>
                          {currencyFormat(bill.total_amount, currencyOpts)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ModalPortal>
          <div className="modal" id="infoModal" ref={(c) => (this.modal = c)}>
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content-wrapper">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{this.state.modalTitle}</h5>
                    <button
                      type="button"
                      className="close"
                      data-dismiss="modal"
                      aria-label="Close"
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                  {this.modalBody}
                  <div className="modal-footer">
                    <button
                      type="submit"
                      className="btn btn-complete"
                      data-dismiss="modal"
                    >
                      Ok
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      </>
    );
  }
}

BOAPolicyDetailsView.propTypes = propTypes;

export default BOAPolicyDetailsView;
